// components/IndustryMultiSelect.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { ReactComponent as RemoveX } from "../../assets/icons/remove-x.svg";
import { ReactComponent as PurpleCheck } from '../../assets/icons/purpleCheck.svg';
import { addEllipsisAfter50Words, customReactSelectStyles } from "../../utils/Helper";
import Arrow from "../common/Arrow";
import CommonTooltip from "../common/Tooltip";

const IndustryMultiSelect = ({
  options = [],
  selectedOptions = [],
  onChange = () => {},
  onSave = () => {},
  onPrev = () => {},
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [internalSelected, setInternalSelected] = useState(selectedOptions);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const selectRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    setInternalSelected(selectedOptions);
  }, [selectedOptions]);

  const handleScrollCheck = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight);
    setCanScrollUp(el.scrollTop > 0);
    setShowScrollButtons(el.scrollHeight > el.clientHeight);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    handleScrollCheck();
  }, [internalSelected]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScrollCheck);
    return () => el.removeEventListener("scroll", handleScrollCheck);
  }, []);

  const scrollSelectedBox = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 40;
      scrollRef.current.scrollBy({
        top: direction === "up" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleRemove = (value) => {
    const filtered = internalSelected.filter((opt) => opt.value !== value);
    setInternalSelected(filtered);
    onChange(filtered);
  };

  const handleSelectChange = (selected) => {
    setInternalSelected(selected || []);
    onChange(selected || []);
  };

  const handleTagClick = (label) => {
    setSearchInput(label);
    selectRef.current?.focus();
  };

  // Custom option component
  const CustomOption = (props) => {
    const { isSelected, isFocused, innerRef, innerProps, data } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`custom-option ${isSelected ? "selected" : ""} ${isFocused ? "focused" : ""}`}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
        }}
      >
        <span className="text-left w-[85%]">{data.label}</span>
        {isSelected && <PurpleCheck />}
      </div>
    );
  };

  // Custom multi value (hide default chip)
  const CustomMultiValue = () => null;

  return (
    <>
      <label className="block mb-1 text-primary text-f14 font-semibold text-left">
        Industry{" "}
        <span className="text-[10px]">
          {" (" + (internalSelected?.length ?? 0) + " Selected)"}
        </span>
      </label>

      {internalSelected?.length > 0 && (
        <div className="selected-box-wrapper">
          <div className="selected-box" ref={scrollRef}>
            {internalSelected.map((item) => (
              <div
                key={item.value}
                className="selected-item"
                onClick={() => handleTagClick(item.label)}
              >
                <CommonTooltip id={item.value} content={item.label}>
                  <span className="cursor-pointer font-bold text-[12px]">
                    {addEllipsisAfter50Words(item.label, 2)}
                  </span>
                </CommonTooltip>
                <button onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(item.value);
                }}>
                  <RemoveX className="mr-1" />
                </button>
              </div>
            ))}
          </div>

          {showScrollButtons && (
            <div className="scroll-buttons">
              <div
                className={`${canScrollUp ? "cursor-pointer" : "cursor-not-allowed"}`}
                onClick={() => canScrollUp && scrollSelectedBox("up")}
              >
                <Arrow color={canScrollUp ? "#012234" : "#E3E3E3"} />
              </div>
              <div
                className={`rotate-180 ${canScrollDown ? "cursor-pointer" : "cursor-not-allowed"}`}
                onClick={() => canScrollDown && scrollSelectedBox("down")}
              >
                <Arrow color={canScrollDown ? "#012234" : "#E3E3E3"} />
              </div>
            </div>
          )}
        </div>
      )}

      <Select
        ref={selectRef}
        inputValue={searchInput}
        onInputChange={(value) => setSearchInput(value)}
        options={options}
        isMulti
        styles={customReactSelectStyles}
        value={internalSelected}
        onChange={handleSelectChange}
        placeholder="Type to search for an industry..."
        isSearchable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{
          Option: CustomOption,
          MultiValue: CustomMultiValue,
        }}
      />

      <div className="flex items-center mt-9">
        <button
          className="flex items-center mr-2 gap-4 text-primaryText w-1/2 font-bold"
          onClick={onPrev}
        >
          <span className="relative">Go Back</span>
        </button>
        <button
          disabled={!(internalSelected?.length)}
          className={`relative group w-full py-3 text-center text-sm font-semibold text-white rounded-md inline-flex items-center justify-center gap-4 ${internalSelected?.length > 0 ? "bg-primary cursor-pointer" : "bg-disable cursor-not-allowed"}`}
          onClick={() => internalSelected?.length > 0 && onSave(internalSelected)}
        >
          <span className="relative">Save & Continue</span>
        </button>
      </div>
    </>
  );
};

export default IndustryMultiSelect;
